import { useEffect, useMemo, useState } from "react";
import type { ColorChangeHandler } from "react-color";
import { Button, Empty, InputNumber, Tooltip } from "antd";
import {
  AudioOutlined,
  FileImageOutlined,
  SwapOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import type { RcFile } from "antd/es/upload/interface";
import UploadWithTextarea from "../../Upload";
import { ColorPicker } from "../../../BaseComps/ColorPicker";
import type {
  ApplyDocumentDuration,
  ApplyDocumentSize,
  DocumentColor,
  DocumentMediaAsset,
  DocumentMediaKind,
  OpenVisualPropertyConnector,
  ReplaceDocumentColor,
  ReplaceDocumentMedia,
} from "./types";
import { ConnectorHoverPopover, PanelSection } from "./controls";

const INITIAL_COLOR_LIMIT = 10;
const EXPANDED_COLOR_LIMIT = 20;

type DocumentSummary = {
  width: number;
  height: number;
  frameRate: number;
  durationFrames: number;
};

type DocumentGeneralSectionProps = {
  summary: DocumentSummary;
  onSizeChange: ApplyDocumentSize;
  onDurationChange: ApplyDocumentDuration;
};

type DocumentColorsSectionProps = {
  colors: DocumentColor[] | undefined;
  status: "idle" | "loading" | "ready" | "error";
  onOpenConnector?: OpenVisualPropertyConnector;
  onReplaceColor: ReplaceDocumentColor;
};

type DocumentMediaSectionProps = {
  assets: DocumentMediaAsset[];
  onReplaceMedia: ReplaceDocumentMedia;
  uploadAssetsToProject: (
    file: RcFile,
    fileType: string,
    callback: (key: string) => void,
    pcb?: any
  ) => Promise<void>;
};

const MEDIA_KIND_LABELS: Record<DocumentMediaKind, string> = {
  image: "Images",
  video: "Video",
  audio: "Audio",
};

const MEDIA_KIND_ICONS: Record<DocumentMediaKind, JSX.Element> = {
  image: <FileImageOutlined />,
  video: <VideoCameraOutlined />,
  audio: <AudioOutlined />,
};

const getDocumentColorStableKey = (color: DocumentColor) =>
  color.references
    .map(
      (reference) =>
        `${reference.assetId ?? "main"}:${reference.layerPath}:${
          reference.path
        }`
    )
    .sort()
    .join("|") || color.id;

const isValidHexColor = (value: string | undefined): value is string =>
  typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value);

const getDocumentColorHex = (color: DocumentColor): string | undefined =>
  color.color?.hex;

const formatColorTooltip = (color: DocumentColor) => {
  const connectedLabel = color.connected ? " - controlled" : "";
  const hex = getDocumentColorHex(color);
  return `${
    isValidHexColor(hex) ? hex.toUpperCase() : "Unknown color"
  }${connectedLabel}`;
};

const roundToTwoDecimals = (value: number) => Math.round(value * 100) / 100;

function DocumentColorSwatch({
  color,
  onOpenConnector,
  onReplaceColor,
}: {
  color: DocumentColor;
  onOpenConnector?: OpenVisualPropertyConnector;
  onReplaceColor: ReplaceDocumentColor;
}) {
  const currentColor = getDocumentColorHex(color);
  const [draftColor, setDraftColor] = useState(currentColor);

  useEffect(() => {
    if (isValidHexColor(currentColor)) {
      setDraftColor(currentColor);
    }
  }, [currentColor]);

  if (!isValidHexColor(draftColor)) {
    return null;
  }

  const handleColorChange: ColorChangeHandler = (nextColor, event) => {
    const nextHex = nextColor.hex?.toLowerCase();
    const sourceHex = isValidHexColor(currentColor)
      ? currentColor.toLowerCase()
      : draftColor.toLowerCase();

    if (!event || !isValidHexColor(nextHex) || nextHex === sourceHex) {
      return;
    }

    setDraftColor(nextHex);
    onReplaceColor(color, nextHex);
  };
  const handleOpenConnector =
    onOpenConnector && currentColor
      ? () =>
          onOpenConnector([], {
            preferredType: "documentColor",
            selectedColor: currentColor,
          })
      : undefined;

  return (
    <ConnectorHoverPopover onConnect={handleOpenConnector}>
      <Tooltip title={formatColorTooltip(color)}>
        <div
          className={`layer-visual-panel__document-color${
            color.connected
              ? " layer-visual-panel__document-color--connected"
              : ""
          }`}
        >
          <ColorPicker
            color={draftColor}
            onChange={handleColorChange}
            size={2}
          />
        </div>
      </Tooltip>
    </ConnectorHoverPopover>
  );
}

function DocumentMediaAssetRow({
  asset,
  expanded,
  onToggle,
  onReplaceMedia,
  uploadAssetsToProject,
}: {
  asset: DocumentMediaAsset;
  expanded: boolean;
  onToggle: () => void;
  onReplaceMedia: ReplaceDocumentMedia;
  uploadAssetsToProject: DocumentMediaSectionProps["uploadAssetsToProject"];
}) {
  const [draftUrl, setDraftUrl] = useState(asset.currentUrl);
  const shouldShowLayerName =
    asset.layerName.trim().toLowerCase() !==
    asset.assetName.trim().toLowerCase();

  useEffect(() => {
    setDraftUrl(asset.currentUrl);
  }, [asset.currentUrl]);

  const commitMediaUrl = (value: string) => {
    setDraftUrl(value);
    const trimmedValue = value.trim();

    if (trimmedValue && trimmedValue !== asset.currentUrl) {
      onReplaceMedia(asset, { url: trimmedValue });
    }
  };

  return (
    <div className="layer-visual-panel__document-asset">
      <button
        type="button"
        className="layer-visual-panel__document-asset-trigger"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="layer-visual-panel__document-asset-icon">
          {asset.kind === "image" && asset.currentUrl ? (
            <img src={asset.currentUrl} alt="" />
          ) : (
            MEDIA_KIND_ICONS[asset.kind]
          )}
        </span>
        <span className="layer-visual-panel__document-asset-copy">
          <span className="layer-visual-panel__document-asset-name">
            {asset.assetName}
          </span>
          {shouldShowLayerName && (
            <span className="layer-visual-panel__document-asset-layer">
              {asset.layerName}
            </span>
          )}
        </span>
        {asset.occurrenceCount > 1 && (
          <span className="layer-visual-panel__document-asset-count">
            {asset.occurrenceCount}
          </span>
        )}
        <SwapOutlined className="layer-visual-panel__document-asset-action" />
      </button>
      {expanded && (
        <div className="layer-visual-panel__document-asset-upload">
          <UploadWithTextarea
            uploadAssetsToProject={uploadAssetsToProject}
            onChange={commitMediaUrl}
            value={draftUrl}
            mimeType={asset.kind}
            placeholder={`Paste ${asset.kind} URL`}
          />
        </div>
      )}
    </div>
  );
}

export function DocumentGeneralSection({
  summary,
  onSizeChange,
  onDurationChange,
}: DocumentGeneralSectionProps) {
  const [durationUnit, setDurationUnit] = useState<"frames" | "seconds">(
    "frames"
  );
  const frameRate = summary.frameRate > 0 ? summary.frameRate : 1;
  const durationValue =
    durationUnit === "frames"
      ? summary.durationFrames
      : roundToTwoDecimals(summary.durationFrames / frameRate);

  const commitDurationValue = (value: number) => {
    onDurationChange(durationUnit === "frames" ? value : value * frameRate);
  };

  return (
    <section className="layer-visual-panel__section">
      <div className="layer-visual-panel__section-body">
        <div className="layer-visual-panel__row">
          <div className="layer-visual-panel__row-label">Size</div>
          <div className="layer-visual-panel__row-control">
            <div className="layer-visual-panel__split-controls">
              <label className="layer-visual-panel__axis-control">
                <span className="layer-visual-panel__axis-label">X</span>
                <InputNumber
                  className="breakpoint-input layer-visual-panel__number-input"
                  min={1}
                  value={summary.width}
                  onChange={(value) => {
                    if (typeof value === "number") {
                      onSizeChange("width", value);
                    }
                  }}
                />
              </label>
              <label className="layer-visual-panel__axis-control">
                <span className="layer-visual-panel__axis-label">Y</span>
                <InputNumber
                  className="breakpoint-input layer-visual-panel__number-input"
                  min={1}
                  value={summary.height}
                  onChange={(value) => {
                    if (typeof value === "number") {
                      onSizeChange("height", value);
                    }
                  }}
                />
              </label>
            </div>
          </div>
        </div>
        <div className="layer-visual-panel__row">
          <div className="layer-visual-panel__row-label">Duration</div>
          <div className="layer-visual-panel__row-control">
            <div className="layer-visual-panel__duration-control">
              <InputNumber
                className="breakpoint-input layer-visual-panel__number-input"
                min={durationUnit === "frames" ? 1 : 0.01}
                step={1}
                value={durationValue}
                onChange={(value) => {
                  if (typeof value === "number") {
                    commitDurationValue(value);
                  }
                }}
              />
              <button
                type="button"
                className="layer-visual-panel__unit-toggle"
                aria-label="Toggle duration unit"
                onClick={() =>
                  setDurationUnit((unit) =>
                    unit === "frames" ? "seconds" : "frames"
                  )
                }
              >
                {durationUnit}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function DocumentColorsSection({
  colors,
  status,
  onOpenConnector,
  onReplaceColor,
}: DocumentColorsSectionProps) {
  const [showAll, setShowAll] = useState(false);
  const visibleLimit = showAll ? EXPANDED_COLOR_LIMIT : INITIAL_COLOR_LIMIT;
  const visibleColors = colors?.slice(0, visibleLimit) ?? [];
  const hasMoreColors = (colors?.length ?? 0) > INITIAL_COLOR_LIMIT;

  if (status === "error") {
    return (
      <PanelSection title="Colors">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Could not inspect colors"
        />
      </PanelSection>
    );
  }

  if (status === "idle" || status === "loading" || !colors) {
    return (
      <PanelSection title="Colors">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Inspecting colors..."
        />
      </PanelSection>
    );
  }

  if (!colors.length) {
    return (
      <PanelSection title="Colors">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No colors" />
      </PanelSection>
    );
  }

  return (
    <PanelSection title="Colors">
      {hasMoreColors && (
        <div className="layer-visual-panel__document-color-toolbar">
          <span>
            {visibleColors.length} of {colors.length}
          </span>
          <Button size="small" onClick={() => setShowAll((value) => !value)}>
            Toggle all
          </Button>
        </div>
      )}
      <div className="layer-visual-panel__document-color-grid">
        {visibleColors.map((color) => (
          <DocumentColorSwatch
            key={getDocumentColorStableKey(color)}
            color={color}
            onOpenConnector={onOpenConnector}
            onReplaceColor={onReplaceColor}
          />
        ))}
      </div>
    </PanelSection>
  );
}

export function DocumentMediaSection({
  assets,
  onReplaceMedia,
  uploadAssetsToProject,
}: DocumentMediaSectionProps) {
  const [expandedAssetId, setExpandedAssetId] = useState<string>();
  const groupedAssets = useMemo(
    () =>
      assets.reduce<Record<DocumentMediaKind, DocumentMediaAsset[]>>(
        (groups, asset) => {
          groups[asset.kind].push(asset);
          return groups;
        },
        { image: [], video: [], audio: [] }
      ),
    [assets]
  );

  useEffect(() => {
    if (
      expandedAssetId &&
      !assets.some((asset) => asset.id === expandedAssetId)
    ) {
      setExpandedAssetId(undefined);
    }
  }, [assets, expandedAssetId]);

  if (!assets.length) {
    return (
      <PanelSection title="Assets">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No media assets"
        />
      </PanelSection>
    );
  }

  return (
    <PanelSection title="Assets">
      {(Object.keys(MEDIA_KIND_LABELS) as DocumentMediaKind[]).map((kind) => {
        const kindAssets = groupedAssets[kind];
        if (!kindAssets.length) {
          return null;
        }

        return (
          <div className="layer-visual-panel__document-asset-group" key={kind}>
            <div className="layer-visual-panel__document-asset-group-title">
              {MEDIA_KIND_LABELS[kind]}
            </div>
            {kindAssets.map((asset) => (
              <DocumentMediaAssetRow
                key={asset.id}
                asset={asset}
                expanded={expandedAssetId === asset.id}
                onToggle={() =>
                  setExpandedAssetId((currentId) =>
                    currentId === asset.id ? undefined : asset.id
                  )
                }
                onReplaceMedia={onReplaceMedia}
                uploadAssetsToProject={uploadAssetsToProject}
              />
            ))}
          </div>
        );
      })}
    </PanelSection>
  );
}
